import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Separator } from './ui/separator';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfServiceModal from './TermsOfServiceModal';
import LanguageSwitcher from './LanguageSwitcher';
import { useBrand } from '../brand/useBrand';

const Footer = () => {
  const { t } = useTranslation();
  const { brand, brandKey, sibling, crossBrandHref } = useBrand();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);

  return (
    <>
      <footer className="bg-gray-50 border-t border-gray-200 mt-auto">
      <div className="max-w-[1760px] mx-auto px-6 sm:px-10 lg:px-20 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Support */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">{t('footer.support')}</h3>
            <ul className="space-y-3">
              <li>
                <a href="/help-center" className="text-gray-600 hover:text-gray-900 text-sm transition-colors" data-testid="footer-help-center">
                  {t('footer.helpCenter')}
                </a>
              </li>
              <li>
                <a href="/aircover" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.aircover')}
                </a>
              </li>
              <li>
                <a href="/safety-information" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.safetyInfo')}
                </a>
              </li>
              <li>
                <a href="/cancellation-options" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.cancellationOptions')}
                </a>
              </li>
            </ul>
          </div>

          {/* Community */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">{t('footer.community')}</h3>
            <ul className="space-y-3">
              <li>
                <a href="/about-montreal" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.aboutMontreal')}
                </a>
              </li>
              <li>
                <a href="/montreal-attractions" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.montrealAttractions')}
                </a>
              </li>
              <li>
                <a href="/things-to-do-montreal" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.thingsToDo')}
                </a>
              </li>
              {/* Gift cards + My Wallet footer links — visible only on
                  marketplace brand. Homesbyli's footer stays 100%
                  stays-focused to avoid the "second gift-card store"
                  perception. */}
              {brandKey === 'marketplace' && (
                <>
                  <li>
                    <a href="/dining" className="text-gray-600 hover:text-gray-900 text-sm transition-colors" data-testid="footer-giftcards">
                      Gift cards
                    </a>
                  </li>
                  <li>
                    <a href="/wallet" className="text-gray-600 hover:text-gray-900 text-sm transition-colors" data-testid="footer-wallet">
                      My Wallet
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Partner with us */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Partner with us</h3>
            <ul className="space-y-3">
              <li>
                <a href="/become-a-partner" className="text-gray-600 hover:text-gray-900 text-sm transition-colors" data-testid="footer-become-partner">
                  Become a Partner
                </a>
              </li>
              <li>
                <a href="/become-host" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.listYourProperty')}
                </a>
              </li>
              <li>
                <a href="/partner/apply" className="text-gray-600 hover:text-gray-900 text-sm transition-colors" data-testid="footer-partner-apply">
                  Offer experiences
                </a>
              </li>
              <li>
                <a href="/host-resources" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.hostingResources')}
                </a>
              </li>
              <li>
                <a href="/host-responsibly" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.hostAgreement')}
                </a>
              </li>
            </ul>
          </div>

          {/* Homesbyli */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Homesbyli</h3>
            <ul className="space-y-3">
              <li>
                <a href="/features" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.newsroom')}
                </a>
              </li>
              <li>
                <a href="/founders-letter" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.foundersLetter')}
                </a>
              </li>
              <li>
                <a href="/careers" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.careers')}
                </a>
              </li>
              <li>
                <a href="/investors" className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  {t('footer.investors')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Cross-brand bridge — Stays brand deliberately does NOT show
            any UCWonder cross-brand card here (user decision 2026-07-03,
            Homesbyli visitors shouldn't be aware of the parent brand).
            Marketplace → Stays direction is preserved so UCWonder can
            still funnel to Homesbyli stays. */}
        {sibling && brandKey === 'marketplace' && (
          <div
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8"
            data-testid="footer-cross-brand-cta"
          >
            <div className="text-sm text-gray-700">
              <span className="font-semibold">Going to Montreal?</span>{' '}
              <span className="text-gray-500">Book a stay at a Homesbyli home.</span>
            </div>
            <a
              href={crossBrandHref('/search')}
              className="inline-flex items-center justify-center text-sm font-semibold rounded-full px-4 py-2 hover:opacity-90 transition-opacity"
              style={{ background: '#1F5C42', color: '#fff' }}
              data-testid="footer-cross-brand-cta-link"
            >
              Browse {sibling.displayName} stays →
            </a>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
            {/* "Part of {parent}" byline — Stays brand hides this too
                (user decision 2026-07-03). Kept as a conditional so
                other brand consumers can still opt in. */}
            {brand?.partOfLine && brandKey !== 'stays' && (
              <>
                <span>·</span>
                <a
                  href={crossBrandHref('/')}
                  className="hover:text-gray-900 transition-colors"
                  data-testid="footer-part-of"
                >
                  {brand.partOfLine}
                </a>
              </>
            )}
            <a href="/terms" className="hover:text-gray-900 transition-colors">
              {t('footer.terms')}
            </a>
            <span>·</span>
            <a href="/privacy" className="hover:text-gray-900 transition-colors">
              {t('footer.privacy')}
            </a>
            <span>·</span>
            <a href="/host-agreement" className="hover:text-gray-900 transition-colors">
              {t('footer.hostAgreement')}
            </a>
            <span>·</span>
            <a href="/sitemap" className="hover:text-gray-900 transition-colors">
              {t('footer.sitemap')}
            </a>
          </div>

          <div className="flex items-center gap-6">
            <LanguageSwitcher variant="footer" />
            <button className="text-sm font-semibold hover:text-gray-700 transition-colors">
              $ CAD
            </button>
          </div>
        </div>
      </div>
    </footer>

    {/* Legal Modals */}
    <PrivacyPolicyModal
      isOpen={showPrivacyPolicy}
      onClose={() => setShowPrivacyPolicy(false)}
    />
    <TermsOfServiceModal
      isOpen={showTermsOfService}
      onClose={() => setShowTermsOfService(false)}
    />
    </>
  );
};

export default Footer;
